import { Box, type BoxProps } from '@mui/material';
import { palette } from '../theme/palette';
import { AlertTriangle } from 'lucide-react';

export interface EmergencyBannerProps extends Omit<BoxProps, 'children'> {
  /** ALL-CAPS / sentence eyebrow — the s. 144(9) citation (localized by the caller). */
  eyebrow: string;
  /** Body copy explaining the bypass and dual routing (localized by the caller). */
  children: React.ReactNode;
}

/**
 * Persistent danger-tinted banner shown whenever a file carries the s. 144(9)
 * emergency flag. Ported from the mobile `EmergencyBanner`
 * (`apps/mobile/src/workflow/EmergencyBanner.tsx`) onto the web emergency
 * tokens. Presentational only — the caller supplies localized strings so this
 * primitive stays free of i18n, like every other component in `@munifocus/ui`.
 */
export function EmergencyBanner({ eyebrow, children, sx, ...rest }: EmergencyBannerProps) {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.75,
        borderRadius: 2,
        backgroundColor: palette.semantic.emergencyTint,
        border: `1px solid ${palette.semantic.emergencyBorder}`,
        ...sx,
      }}
      {...rest}
    >
      <Box component="span" sx={{ color: palette.semantic.emergency, display: 'flex', mt: '2px' }}>
        <AlertTriangle size={20} />
      </Box>
      <Box>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: palette.semantic.emergencyInk,
          }}
        >
          {eyebrow}
        </Box>
        <Box
          component="p"
          sx={{
            m: 0,
            mt: 0.75,
            fontSize: 13.5,
            lineHeight: 1.45,
            color: palette.semantic.emergencyInk,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
